import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Placeholder } from "react-bootstrap";
import { connect } from "react-redux";
import { getAuditApi, getAuditReport } from "../../../services/AuditService";
import { renderFileDetails } from "../../../utils";
import ReportStatus from "./ReportStatus";

const AuditDetailsLoading = () => {
  return (
    <div>
      <div className="card">
        <div className="card-body">
          <div className="w-100 px-2 mt-3">
            <Placeholder animation="glow">
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
            </Placeholder>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          <div className="d-flex align-item-center justify-content-between">
            <div className="w-100 px-2">
              <Placeholder animation="glow">
                <Placeholder
                  xs={12}
                  style={{ height: "20px" }}
                  className="rounded my-2"
                />
                <Placeholder
                  xs={12}
                  style={{ height: "20px" }}
                  className="rounded my-2"
                />
              </Placeholder>
            </div>

            <div className="w-100 px-2">
              <Placeholder animation="glow">
                <Placeholder
                  xs={12}
                  style={{ height: "20px" }}
                  className="rounded my-2"
                />
                <Placeholder
                  xs={12}
                  style={{ height: "20px" }}
                  className="rounded my-2"
                />
              </Placeholder>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function AuditDetails({ user }) {
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  const fetchReport = useCallback(async (scheduleId) => {
    try {
      const resp = await getAuditReport(scheduleId);
      setReport(resp.data.results);
    } catch (error) {
      console.error("Error fetching report:", error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const resp = await getAuditApi(id);
      const respData = resp.data.results;
      setData(respData);

      if (respData?.schedules?.length > 0) {
        fetchReport(respData.schedules[0].id);
      }
    } catch (error) {
      console.error("Error fetching audit data:", error);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [id, fetchReport]);

  useEffect(() => {
    fetchData();
  }, [id, fetchData]);

  const isScheduledDatePassed = (scheduleAt) => {
    if (!scheduleAt) return false;
    const scheduledDate = new Date(scheduleAt);
    const currentDate = new Date();
    return currentDate >= scheduledDate;
  };

  const handleAfterUpdate = (updatedData) => {
    setReport((prevReport) => ({
      ...prevReport,
      report: {
        ...prevReport?.report,
        status: updatedData.status,
      },
    }));
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-4">
          {isLoading ? (
            <AuditDetailsLoading />
          ) : data ? (
            <div className="card shadow-sm border-0">
              <div className="card-header bg-white border-bottom d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm btn-circle"
                    onClick={() => navigate("/audit")}
                  >
                    <i className="fa-solid fa-arrow-left"></i>
                  </button>
                  <h5 className="mb-0 ms-2">Audit Details</h5>
                </div>
                {data.created_by?.id === user?.id &&
                  (data?.status === "pending" || data?.status === "rejected") && (
                    <div>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() =>
                          navigate(`/edit-audit`, { state: { data: data } })
                        }
                      >
                        <i className="fa-solid fa-pen-to-square"></i> Edit
                      </button>
                    </div>
                  )}
              </div>
              <div className="card-body">
                <ul className="list-unstyled">
                  <li className="mb-3">
                    <strong>Title:</strong> {data?.title}
                  </li>
                  <li className="mb-3">
                    <strong>Status:</strong>{" "}
                    {data?.status === "pending" ? (
                      <span className="badge light border-0 badge-warning badge-sm">
                        Pending
                      </span>
                    ) : data?.status === "approved" ? (
                      <span className="badge light border-0 badge-info badge-sm">
                        Approved
                      </span>
                    ) : (
                      <span className="badge light border-0 badge-danger badge-sm">
                        Rejected
                      </span>
                    )}
                  </li>
                  <li className="mb-3">
                    <strong>Divisions:</strong>
                    <div className="mt-2">
                      <span
                        key={data?.division?.id}
                        className="badge text-black me-2 mb-2"
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.6rem",
                        }}
                      >
                        <i className="fa-solid fa-building me-1"></i>{" "}
                        {data?.division?.name}
                      </span>
                    </div>
                  </li>
                </ul>
                {data.status === "approved" &&
                  !report?.report &&
                  isScheduledDatePassed(data.schedules[0]?.schedule_at) && (
                    <button
                      className="btn btn-outline-primary w-100 mt-3"
                      onClick={() =>
                        navigate(`/audit-report`, { state: { data: data } })
                      }
                    >
                      <i className="fa-solid fa-file-pdf me-2"></i>Generate
                      Report
                    </button>
                  )}
              </div>
            </div>
          ) : (
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <h4 className="text-muted">
                  ⚠️ Audit Not Found! <br /> ID {id}
                </h4>
              </div>
            </div>
          )}
        </div>

        <div className="col-md-8">
          <div className="card shadow-sm border-0">
            <div className="card-header">
              <h5 className="mb-2">Schedules</h5>
            </div>
            <div className="card-body">
              <div>
                <ul className="list-unstyled">
                  {data?.schedules?.map((item, index) => (
                    <li key={index} className="mb-4">
                      <div className="row px-3">
                        <div className="col-md-6 mb-2">
                          <p>
                            <i className="fa-solid fa-calendar-check text-primary me-2"></i>
                            <strong>Status:</strong> {item?.status}
                          </p>
                          <p>
                            <i className="fa-solid fa-clock text-secondary me-2"></i>
                            <strong>Schedule At:</strong> {item?.schedule_at}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <p>
                            <i className="fa-solid fa-user text-success me-2"></i>
                            <strong>Auditor:</strong>{" "}
                            {item?.auditor?.user?.name}
                          </p>
                          <p>
                            <i className="fa-solid fa-envelope text-danger me-2"></i>
                            <strong>Email:</strong> {item?.auditor?.user?.email}
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                {!report?.report ? (
                  <>
                    <h5>Checklist</h5>
                    <hr />
                    <div className="row">
                      {data?.checklist?.map((item, index) => (
                        <div key={index} className="col-md-6 mb-3 px-4">
                          <p>
                            <i className="fa-solid fa-file-alt text-info me-2"></i>
                            <strong>Title:</strong> {item?.title}
                          </p>
                          <p>
                            <i className="fa-solid fa-align-left text-secondary me-2"></i>
                            <strong>Description:</strong> {item?.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="d-flex align-items-center justify-content-between">
                      <h5>Checklist & Report</h5>
                      {report?.report && (
                        <ReportStatus
                          data={report}
                          handleAfterUpdate={handleAfterUpdate}
                        />
                      )}
                    </div>
                    <hr />
                    <div className="row">
                      {report?.observations?.map((item) => (
                        <div className="col-md-6 mb-3 px-4">
                          <p>
                            <i className="fa-solid fa-file-alt text-info me-2"></i>
                            <strong>Title:</strong> {item?.checklist?.title}
                          </p>
                          <p>
                            <i className="fa-solid fa-align-left text-secondary me-2"></i>
                            <strong>Description:</strong>{" "}
                            {item?.checklist?.description}
                          </p>
                          <div>{renderFileDetails(item?.evidence)}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  user: state.auth?.user,
});
export default connect(mapStateToProps)(AuditDetails);
